import type { CampaignProduct, SalesReviewSubmission, SalesSampleLine } from '../../types/domain';

export interface CorrectionDeal {
  id: string;
  parent: string;
  parentName: string;
  qualifyingQuantity?: number;
  sampleQuantity?: number;
  products: Array<{ id: string; name: string; sku: string }>;
}

export function sampleKey(deal: string, product: string) { return `${deal}:${product}`; }

export function effectiveSamples(report: SalesReviewSubmission): SalesSampleLine[] {
  return report.effective_samples ?? report.source_submissions.flatMap((source) => source.samples ?? []);
}

export function sampleDraft(report: SalesReviewSubmission): Record<string, string> {
  const quantities: Record<string, string> = {};
  for (const sample of effectiveSamples(report)) {
    const key = sampleKey(sample.deal_id, sample.product_id);
    quantities[key] = String(Number(quantities[key] ?? 0) + sample.quantity);
  }
  return quantities;
}

export function correctionDeals(report: SalesReviewSubmission, products: CampaignProduct[]): CorrectionDeal[] {
  const deals = new Map<string, CorrectionDeal>();
  for (const product of products) {
    const deal = product.free_sample_deal;
    if (!deal?.is_active) continue;
    deals.set(deal.id, {
      id: deal.id, parent: product.product, parentName: product.product_name,
      qualifyingQuantity: deal.qualifying_quantity, sampleQuantity: deal.sample_quantity,
      products: deal.sample_product_details.filter((sample) => sample.is_active),
    });
  }
  // Recorded options/ratios remain correctable even after a deal is changed or retired.
  const historical = [...report.source_submissions.flatMap((source) => source.samples ?? []), ...effectiveSamples(report)];
  for (const sample of historical) {
    const deal: CorrectionDeal = deals.get(sample.deal_id) ?? {
      id: sample.deal_id, parent: sample.qualifying_product_id,
      parentName: sample.qualifying_product_name, products: [],
    };
    if (sample.qualifying_quantity_snapshot !== undefined) deal.qualifyingQuantity = sample.qualifying_quantity_snapshot;
    if (sample.sample_quantity_snapshot !== undefined) deal.sampleQuantity = sample.sample_quantity_snapshot;
    if (!deal.products.some((product) => product.id === sample.product_id)) {
      deal.products.push({ id: sample.product_id, name: sample.product_name, sku: sample.product_sku });
    }
    deals.set(deal.id, deal);
  }
  return [...deals.values()];
}

export function sampleAllowance(deal: CorrectionDeal, sold: number): number | null {
  if (!deal.qualifyingQuantity || !deal.sampleQuantity) return null;
  return Math.floor(sold / deal.qualifyingQuantity) * deal.sampleQuantity;
}

export function sampleValidation(deals: CorrectionDeal[], quantities: Record<string, string>, lines: Array<{ product_id: string; quantity: string }>): string | null {
  if (Object.values(quantities).some((value) => !Number.isInteger(Number(value)) || Number(value) < 0 || Number(value) > 1_000_000)) {
    return 'Enter whole-number free sample quantities from 0 to 1,000,000.';
  }
  for (const deal of deals) {
    const sold = Number(lines.find((line) => line.product_id === deal.parent)?.quantity ?? 0);
    const allowance = sampleAllowance(deal, sold);
    const selected = deal.products.reduce((sum, product) => sum + Number(quantities[sampleKey(deal.id, product.id)] ?? 0), 0);
    if (allowance !== null && selected > allowance) return `${deal.parentName} allows at most ${allowance} free samples for the corrected quantity.`;
  }
  return null;
}

export function samplePayload(quantities: Record<string, string>) {
  return Object.entries(quantities).filter(([, quantity]) => Number(quantity) > 0).map(([key, quantity]) => {
    const [deal_id, product_id] = key.split(':');
    return { deal_id, product_id, quantity: Number(quantity) };
  });
}
